import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { api, ApiError } from '../../shared/api/http'
import { useAuth } from './AuthContext'
import { LanguageSelector } from '../../shared/i18n/LanguageSelector'
import { useLocale } from '../../shared/i18n/LocaleContext'

export function LoginPage({ onSignedIn }: { onSignedIn?: () => void } = {}) {
  const auth = useAuth()
  const { t } = useLocale()
  const navigate = useNavigate()
  const [loginValue, setLoginValue] = useState('')
  const [password, setPassword] = useState('')
  const [secondFactorCode, setSecondFactorCode] = useState('')
  const [needsTotp, setNeedsTotp] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [showPassword,setShowPassword]=useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [mustChange, setMustChange] = useState(false)
  const [needsEmail, setNeedsEmail] = useState(false)
  const [firstEmail, setFirstEmail] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      if (mustChange) {
        await api('/auth/change-temporary-password', { method: 'POST', body: JSON.stringify({ login: loginValue.trim(), password, newPassword, passwordConfirmation: confirmation, secondFactorCode: secondFactorCode.trim() || null, email: needsEmail ? firstEmail.trim() : null }) })
        setPassword(''); setNewPassword(''); setConfirmation(''); setSecondFactorCode(''); setMustChange(false)
        setError('Senha alterada. Entre com a nova senha.'); return
      }
      await auth.login(loginValue.trim(), password, rememberMe, needsTotp ? secondFactorCode.trim() : undefined)
      if (onSignedIn) onSignedIn()
      else navigate('/modulos')
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : 'generic_error'
      if (code === 'password_change_required') { setMustChange(true); setError('Defina uma nova senha pessoal para acessar.'); return }
      if (code === 'first_access_required') { setMustChange(true); setNeedsEmail(true); setError('Informe seu e-mail e defina uma nova senha pessoal.'); return }
      if (code === 'email_required' || code === 'invalid_email' || code === 'email_unavailable') { setError(code === 'email_unavailable' ? 'Esse e-mail já está em uso.' : 'Informe um e-mail válido.'); return }
      const resetErrors: Record<string, string> = { weak_password: 'Use pelo menos 6 caracteres, maiúscula, minúscula, número e símbolo.', password_mismatch: 'As senhas não coincidem.', password_must_differ: 'A nova senha precisa ser diferente da temporária.' }
      if (resetErrors[code]) { setError(resetErrors[code]); return }
      if (code === 'second_factor_required') setNeedsTotp(true)
      const messageKey = code === 'invalid_credentials' ? 'invalidCredentials' : code === 'second_factor_required' ? 'secondFactorRequired' : code === 'invalid_second_factor' ? 'invalidSecondFactor' : code === 'account_suspended' ? 'accountSuspended' : 'signInError'
      setError(t(messageKey))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="login-page">
      <section className="login-visual">
        <div className="visual-grid" />
        <div className="visual-copy">
          <span className="eyebrow">{t('intelligentMonitoring')}</span>
          <h1>{t('loginHero')}</h1>
          <p>{t('loginHeroText')}</p>
          <div className="signal-row"><span>{t('activeOperation')}</span><span>{t('protectedConnection')}</span></div>
        </div>
      </section>
      <section className="login-panel">
        <form onSubmit={submit}>
          <div className="login-brand"><img src="/logo.png" alt="" /><strong>Spectra Guard AI</strong><LanguageSelector compact /></div>
          <span className="eyebrow blue">{t('centralAccess')}</span>
          <h2>{mustChange ? 'Troca obrigatória de senha' : t('welcomeBack')}</h2>
          <p>{t('credentialsHelp')}</p>
          <label>{t('login')}<input autoFocus autoComplete="username" value={loginValue} onChange={(e) => setLoginValue(e.target.value)} required /></label>
          <label>{t('password')}<span className="password-field"><input type={showPassword?'text':'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /><button type="button" aria-label={showPassword?t('hidePassword'):t('showPassword')} onClick={()=>setShowPassword(value=>!value)}>{showPassword?'◉':'◎'}</button></span></label>
          {needsTotp && <label>{t('authenticationCode')}<input inputMode="numeric" autoComplete="one-time-code" value={secondFactorCode} onChange={(e) => setSecondFactorCode(e.target.value)} required /></label>}
          {mustChange && needsEmail && <label>Seu e-mail<input type="email" autoComplete="email" required maxLength={254} value={firstEmail} onChange={e => setFirstEmail(e.target.value)} /></label>}
          {mustChange && <><label>Nova senha<input type="password" autoComplete="new-password" required minLength={6} maxLength={256} value={newPassword} onChange={e => setNewPassword(e.target.value)} /></label><label>Confirmar nova senha<input type="password" autoComplete="new-password" required maxLength={256} value={confirmation} onChange={e => setConfirmation(e.target.value)} /></label><small>Use maiúscula, minúscula, número e símbolo.</small></>}
          <div className="login-options"><label className="check"><input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} /> {t('remember')}</label><Link to="/recuperar-senha">{t('forgot')}</Link></div>
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="primary" disabled={busy}>{busy ? t('signingIn') : mustChange ? 'Salvar nova senha' : t('signIn')}</button>
          <small>Seu acesso é criado pelo administrador da empresa.</small>{' '}
          <Link to="/portaria/morador">Acesso do morador da Portaria</Link>
          <small className="secure-note">{t('secureSession')}</small>
        </form>
      </section>
    </div>
  )
}
